
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import Project1Image from "@/assets/images/1-1Remodel.png";
import Project2Image from "@/assets/images/2Landscape Lighting.png";
import Project3Image from "@/assets/images/3ELV_DEVICE.png";
import Project4Image from "@/assets/images/4ProjectImage.png";

export default function PortfolioSectionA() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [expandedProject, setExpandedProject] = useState<number | null>(null);

  const projects = [
    {
      id: 1,
      title: "Modern Condo Interior Upgrade",
      category: "Interior Remodel",
      slug: "Modern_Condo_Interior",
      image: Project1Image,
      description:
        "A modern interior remodel of residential condos focusing on functional upgrades and aesthetic enhancements",
    },
    {
      id: 2,
      title: 'Landscape Lighting Layout',
      category: 'Landscape Lighting',
      slug: 'Landscape_Lighting_Layout',
      image: Project2Image,
      description: 'This project focuses on the strategic planning and design of outdoor lighting to enhance both functionality and visual appeal.',
    },
     {
      id: 3,
      title: 'ELV Device Layout',
      category: 'ELV Layout',
      slug: 'ELV_Layout',
      image: Project3Image,
      description: 'This layout design focuses on the placement and coordination of Extra Low Voltage (ELV) systems, including CCTV, access control, data, and communication devices.',
    },
    {
      id: 4,
      title: 'Pre-Wire',
      category: 'Landscape Lighting',
      slug: 'Landscape_Lighting',
      image: Project4Image,
      description: 'This project focuses on the strategic planning and design of outdoor lighting to enhance both functionality and visual appeal.',
    },
  ];

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % projects.length);
    setTimeout(() => setIsTransitioning(false), 800);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + projects.length) % projects.length);
    setTimeout(() => setIsTransitioning(false), 800);
  };

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 800);
  };

  const expandProject = (projectId: number) => {
    setExpandedProject(projectId);
  };

  const closeExpanded = () => {
    setExpandedProject(null);
  };

  // Get visible projects (current, previous, next)
  const getVisibleProjects = () => {
    const prevIndex = (currentSlide - 1 + projects.length) % projects.length;
    const nextIndex = (currentSlide + 1) % projects.length;
    
    return {
      prev: projects[prevIndex],
      current: projects[currentSlide],
      next: projects[nextIndex]
    };
  };

  const visibleProjects = getVisibleProjects();

  return (
    <section id="portfolio" className="py-20 bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#e18033] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#dbcfbf] rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Portfolio Showcase
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Explore our latest 3D visualizations and architectural renderings
          </p>
        </div>

        {/* Cinematic Carousel */}
        <div className="relative h-[600px] md:h-[700px] mb-12">
          {/* Main Carousel Container */}
          <div className="relative w-full h-full flex items-center justify-center">
            
            {/* Previous Project (Left) */}
            <div 
              className={`absolute left-0 w-[30%] h-[80%] cursor-pointer transition-all duration-800 ease-in-out transform ${
                isTransitioning ? 'scale-95 opacity-60' : 'scale-100 opacity-70 hover:opacity-90 hover:scale-105'
              }`}
              style={{ 
                transform: `translateX(-10%) translateZ(-100px) rotateY(15deg)`,
                filter: 'blur(1px)'
              }}
              onClick={() => goToSlide((currentSlide - 1 + projects.length) % projects.length)}
            >
              <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={visibleProjects.prev.image} 
                  alt={visibleProjects.prev.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <p className="text-sm font-medium opacity-80">{visibleProjects.prev.category}</p>
                  <h3 className="text-lg font-bold">{visibleProjects.prev.title}</h3>
                </div>
              </div>
            </div>

            {/* Current Project (Center) */}
            <div 
              className={`relative w-[70%] h-full cursor-pointer transition-all duration-800 ease-in-out transform ${
                isTransitioning ? 'scale-95' : 'scale-100 hover:scale-105'
              }`}
              style={{ zIndex: 10 }}
              onClick={() => expandProject(visibleProjects.current.id)}
            >
              <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-3xl">
                <img 
                  src={visibleProjects.current.image} 
                  alt={visibleProjects.current.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                
                {/* Project Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <span className="inline-block px-4 py-2 bg-[#e18033] text-sm font-medium rounded-full mb-4">
                    {visibleProjects.current.category}
                  </span>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4">
                    {visibleProjects.current.title}
                  </h3>
                  <p className="text-gray-200 text-lg leading-relaxed max-w-2xl">
                    {visibleProjects.current.description}
                  </p>
                </div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute inset-0 bg-gradient-to-t from-[#e18033]/20 to-transparent"></div>
                </div>
              </div>
            </div>

            {/* Next Project (Right) */}
            <div 
              className={`absolute right-0 w-[30%] h-[80%] cursor-pointer transition-all duration-800 ease-in-out transform ${
                isTransitioning ? 'scale-95 opacity-60' : 'scale-100 opacity-70 hover:opacity-90 hover:scale-105'
              }`}
              style={{ 
                transform: `translateX(10%) translateZ(-100px) rotateY(-15deg)`,
                filter: 'blur(1px)'
              }}
              onClick={() => goToSlide((currentSlide + 1) % projects.length)}
            >
              <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={visibleProjects.next.image} 
                  alt={visibleProjects.next.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-6 right-6 text-white text-right">
                  <p className="text-sm font-medium opacity-80">{visibleProjects.next.category}</p>
                  <h3 className="text-lg font-bold">{visibleProjects.next.title}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button 
            onClick={prevSlide}
            disabled={isTransitioning}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer disabled:opacity-50 z-20"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-arrow-left-line text-white text-2xl"></i>
            </div>
          </button>
          
          <button 
            onClick={nextSlide}
            disabled={isTransitioning}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer disabled:opacity-50 z-20"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-arrow-right-line text-white text-2xl"></i>
            </div>
          </button>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center space-x-3 mb-12">
          {projects.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer disabled:opacity-50 ${
                index === currentSlide 
                  ? 'bg-[#e18033] scale-125' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center flex justify-center items-center">
          <Button 
            variant="outline" 
            size="lg" 
            className="border-[#e18033] text-[#e18033] hover:bg-[#e18033] hover:text-white transition-all duration-300"
            onClick={() => window.location.href = '/portfolio'}
          >
            <div className="w-5 h-5 flex items-center justify-center mr-2">
              <i className="ri-gallery-line"></i>
            </div>
            View Complete Portfolio
          </Button>
        </div>
      </div>

      {/* Expanded Project Modal */}
      {expandedProject && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2a2a2a] to-[#1a1a1a] rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-auto">
            {(() => {
              const project = projects.find(p => p.id === expandedProject);
              if (!project) return null;
              
              return (
                <>
                  <div className="relative">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover rounded-t-3xl"
                    />
                    <button 
                      onClick={closeExpanded}
                      className="absolute top-6 right-6 w-12 h-12 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer transition-all duration-300"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-close-line text-white text-2xl"></i>
                      </div>
                    </button>
                    <div className="absolute top-6 left-6 px-4 py-2 bg-[#e18033] text-white text-sm font-medium rounded-full">
                      {project.category}
                    </div>
                  </div>
                  <div className="p-8">
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      {project.title}
                    </h3>
                   
                 
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </section>
  );
}
